import Image from '@/components/atoms/image/image';
import RichText from '@/components/atoms/rich-text/rich-text';
import Typography from '@/components/atoms/typography/typography';
import variables from '@/components/modules/spot/spot.variables';
import Splash from '@/components/molecules/splash/splash';
import CtaLinkList from '@/components/organisms/cta-link-list/cta-link-list';
import Column from '@/components/utilities/column/column';
import Container from '@/components/utilities/container/container';
import ModuleWrapper, {
  ModuleWrapperProps,
} from '@/components/utilities/module-wrapper/module-wrapper';
import Row from '@/components/utilities/row/row';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import styles from './spot.module.scss';

export interface SpotProps extends ModuleWrapperProps {
  /** The links to show */
  ctaLinks?: Content.Components['SpotModuleDto']['ctaLinks'];
  /** The headline of the module */
  headline: Content.Components['SpotModuleDto']['headline'];
  /** The image to show */
  image: Content.Components['SpotModuleDto']['image'];
  /** The pre headline to show on the left side */
  leftPreHeadline: Content.Components['SpotModuleDto']['leftPreHeadline'];
  /** The pre headline to show on the right side */
  rightPreHeadline: Content.Components['SpotModuleDto']['rightPreHeadline'];
  /** The splash to display */
  splash: Content.Components['SpotModuleDto']['splash'];
  /** The sub headline of the module */
  subHeadline: Content.Components['SpotModuleDto']['subHeadline'];
  /** The text to show */
  text: Content.Components['SpotModuleDto']['text'];
}

const Spot = ({
  ctaLinks = [],
  headline,
  image,
  leftPreHeadline,
  rightPreHeadline,
  splash,
  subHeadline,
  text,
  ...rest
}: SpotProps) => {
  const isTablet = useWindowSize('tablet');

  return (
    <ModuleWrapper
      {...rest}
      className={styles.component}
      padding={!isTablet ? 'none' : undefined}
    >
      <Container className={styles.container}>
        <Row className={styles.row}>
          <Column
            className={styles.headlineColumn}
            offset={{ tablet: 1 }}
            width={{ desktop: 3, tablet: 8 }}
          >
            {leftPreHeadline && (
              <Typography className={styles.preHeadline} tagStyle="bodyMedium">
                {leftPreHeadline}
              </Typography>
            )}

            <Typography className={styles.headline} tagStyle="displayMedium">
              {headline}
            </Typography>
          </Column>

          <Column
            className={styles.imageColumn}
            offset={{ desktop: 0, tablet: 1 }}
            width={{ desktop: 4, tablet: 5 }}
          >
            <div className={styles.imageWrapper}>
              <Image
                alt={image.altText}
                aspectRatio="threeByFour"
                className={styles.image}
                focalPoint={image.focalPoint}
                height={437}
                src={image.url}
                srcSet={{
                  desktop: {
                    height: 569,
                    width: 427,
                  },
                  laptop: {
                    height: 500,
                    width: 375,
                  },
                  tablet: {
                    height: 381,
                    width: 286,
                  },
                }}
                unoptimized
                width={328}
              />

              {splash && <Splash className={styles.splash} {...splash} />}
            </div>
          </Column>

          <Column
            className={styles.textColumn}
            width={{ desktop: 3, tablet: 5 }}
          >
            <div className={styles.rightHeadlineWrapper}>
              {rightPreHeadline && (
                <Typography
                  className={styles.preHeadline}
                  tagStyle="subHeading"
                >
                  {rightPreHeadline}
                </Typography>
              )}

              <Typography
                className={styles.subHeadline}
                tagStyle={variables.subHeadlineTagStyle}
              >
                {subHeadline}
              </Typography>
            </div>

            {text && <RichText className={styles.text} text={text} />}

            {ctaLinks.length > 0 && (
              <div className={styles.links}>
                <CtaLinkList items={ctaLinks} />
              </div>
            )}
          </Column>
        </Row>
      </Container>
    </ModuleWrapper>
  );
};

Spot.displayName = 'Spot';

export default Spot;
